/**
 * SIL-1828 (R2) - dependency-free home of the "Directional" derivation so the
 * server (fmv-resolution.ts, API routes) and the shared render code (passport
 * document/body) can import it without pulling the FMV engine into a UI bundle.
 */

/** SIL-1825: baseline_price_source id for a directional sum-of-components price. */
export const VIRTUAL_COMP_SOURCE_ID = 'virtual_comp' as const;

/**
 * The label every surface shows next to a value that derives from a virtual
 * baseline. Also the `baseline_price_source.name` of the 'virtual_comp' row
 * (migration 1063).
 */
export const DIRECTIONAL_BASELINE_LABEL = 'Directional estimate (sum of components)';

/**
 * The ONE server-side derivation of the "Directional" flag. Every DTO carries
 * the result as `isDirectionalBaseline`; UIs read that boolean and never
 * compare baselinePriceSourceId themselves.
 */
export function isDirectionalBaseline(baselinePriceSourceId: string | null | undefined): boolean {
  return baselinePriceSourceId === VIRTUAL_COMP_SOURCE_ID;
}

/**
 * SIL-1848: API error code when a listing has no seller-supplied price and the
 * only available default derives from a directional (virtual) baseline.
 */
export const DIRECTIONAL_PRICE_ONLY_CODE = 'directional_price_only' as const;

/** Customer-safe reason shown instead of a prefilled price. */
export const DIRECTIONAL_PRICE_ONLY_MESSAGE =
  'Only a directional estimate exists for this hardware (no market comps yet), so no default price is offered. Enter your own asking price.';

export type DefaultListingPrice =
  | { kind: 'price'; priceUsd: number }
  | { kind: 'none'; reason: 'no_fmv' | typeof DIRECTIONAL_PRICE_ONLY_CODE };

/**
 * SIL-1848: the ONE place a default / suggested / prefilled listing price is
 * derived from a snapshot median. A directional (virtual) median is a
 * sum-of-components estimate, never a market price, so it is refused as a
 * default. Callers use this only when the seller supplied no price.
 *
 * `fmvMedUsd` null / non-positive => 'no_fmv'. `directional` is the
 * server-derived boolean (isDirectionalBaseline / includesDirectionalBaseline).
 */
export function resolveDefaultListingPrice(input: {
  fmvMedUsd: number | null | undefined;
  directional: boolean;
}): DefaultListingPrice {
  if (input.fmvMedUsd == null || !Number.isFinite(input.fmvMedUsd) || input.fmvMedUsd <= 0) {
    return { kind: 'none', reason: 'no_fmv' };
  }
  if (input.directional) return { kind: 'none', reason: DIRECTIONAL_PRICE_ONLY_CODE };
  return { kind: 'price', priceUsd: input.fmvMedUsd };
}
